import { ObjectFieldTemplateProps } from "@rjsf/utils/lib/types";

// Helper function to get nested property values
export const getNestedValue = (obj: any, path: string): any => {
  try {
    const keys = path
      .split(/[.[\]]/)
      .filter(Boolean)
      .map(key => key.replace(/^['"]|['"]$/g, '')); // Strip surrounding quotes

    return keys.reduce((current, key) => {
      if (current == null) return undefined;

      // If current is an array and key is not a numeric index, search for locale
      if (Array.isArray(current) && isNaN(Number(key))) {
        const found = current.find(item => item?.locale === key);
        return found;
      }

      return current[key];
    }, obj);
  } catch {
    return undefined;
  }
};

// Extract level from the id path (e.g., "root_levels_0" -> level 1)
export const extractLevelFromId = (idSchema?: any): number | undefined => {
  if (!idSchema?.$id) return undefined;

  const match = idSchema.$id.match(/root_levels_(\d+)/);
  if (match) {
    return parseInt(match[1]) + 1; // Convert 0-based to 1-based
  }
  return undefined;
};

// Helper function to process title template with variables
export const processTemplate = (template: string, data: any, idSchema?: any): string => {
  // Create enhanced data object with calculated level
  const enhancedData = {
    ...data,
    level: extractLevelFromId(idSchema) || data?.level,
  };

  return template
    .replace(/\{([^}]+)\}/g, (_, path) => {
      const value = getNestedValue(enhancedData, path.trim());
      return value !== undefined && value !== null && value !== "" ? String(value) : "";
    })
    .trim();
};

// Get the first error message for this object or its children
export const getFirstError = (props: ObjectFieldTemplateProps): string | null => {
  if (!props.formData || !props.schema) return null;

  // Check required fields and generate error messages
  const required = props.schema.required || [];
  const requiredFieldError = required.find(fieldName => {
    const value = props.formData[fieldName];
    return value === undefined || value === null || value === "";
  });

  if (requiredFieldError) {
    const fieldSchema = props.schema.properties?.[requiredFieldError];
    const fieldTitle =
      fieldSchema && typeof fieldSchema === "object" && "title" in fieldSchema ? fieldSchema.title : requiredFieldError;
    return `${fieldTitle} is required`;
  }

  // Check if any child properties contain error classes or attributes
  // This is a simplified approach - in a real implementation you'd want
  // access to the actual validation errors from RJSF
  if (props.properties) {
    const errorProp = props.properties.find(prop => {
      const contentString = prop.content?.toString() || "";
      return contentString.includes("error") || contentString.includes("invalid");
    });

    if (errorProp) {
      return "Validation error in field";
    }
  }

  return null;
};

// Truncate error message to 30 characters with ellipsis
export const truncateError = (error: string): string => {
  return error.length > 30 ? error.substring(0, 27) + "..." : error;
};
